import { tGlobal } from '@/i18n';
import { logError } from '@/lib/logError';
import rest from '@/lib/rest';
import type { Stripe, StripeElements, StripeError, StripePaymentElement } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { defineStore } from 'pinia';
import { useUserStore } from './user';

interface BillingDetailsInput {
  name?: string;
  email?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
}

/**
 * When the Payment Element opts out of address collection
 * (fields.billingDetails.address = 'never'), Stripe requires EVERY address
 * sub-field to be present in confirmParams — including ones the address has no
 * value for, like `state` outside the US ("did not pass ...address.state").
 * Fill the gaps with empty strings so a complete-but-stateless address passes
 * client-side validation.
 */
function toConfirmBillingDetails(details: BillingDetailsInput) {
  const { address, ...rest } = details;
  if (!address) return rest;
  return {
    ...rest,
    address: {
      line1: address.line1 ?? '',
      line2: address.line2 ?? '',
      city: address.city ?? '',
      state: address.state ?? '',
      postal_code: address.postal_code ?? '',
      country: address.country ?? '',
    },
  };
}

/**
 * Codes Stripe files under `invalid_request_error` that are still the customer's to fix. A
 * failed or cancelled 3-D Secure check on a SetupIntent comes back this way rather than as a
 * `card_error` (DABBLE-ACCOUNTS-2-4G); its message asks for another payment method, and the
 * intent returns to `requires_payment_method`, so the same form can simply be tried again.
 */
const CUSTOMER_FACING_CODES = new Set(['setup_intent_authentication_failure', 'payment_intent_authentication_failure']);

/**
 * The DAB-957 policy for a Stripe.js error, shared by every action that gets one.
 *
 * Card and validation messages are written for the customer, so show them verbatim.
 * Everything else (integration / API errors) is developer-facing, so show the generic
 * string but keep Stripe's code next to it — a support screenshot then identifies the
 * failure instead of saying nothing.
 *
 * Report only the faults. A decline or an incomplete field is a business outcome, not
 * an application error, and logError goes straight to Sentry.captureException —
 * routing routine customer mistakes there would bury the very failures this exists to
 * surface. Everything else reaches nobody otherwise: the callers in `use/stripeCheckout`
 * suppress their toast once `error` is set, and none of them log. Wrapped in a real
 * Error so logError captures it as an exception rather than a bare message.
 *
 * Returns what the customer should read.
 */
function describeAndReportStripeError(error: StripeError, context: string): string {
  handledStripeErrors.add(error);
  const isCustomerFacing =
    error.type === 'card_error' || error.type === 'validation_error' || CUSTOMER_FACING_CODES.has(error.code ?? '');
  const codeSuffix = error.code ? ` (${error.code})` : '';
  if (!isCustomerFacing) {
    logError(new Error(`Stripe ${context} failed: ${error.type}${codeSuffix}`), {
      type: error.type,
      code: error.code,
      declineCode: error.decline_code,
      message: error.message,
    });
  }
  return isCustomerFacing && error.message ? error.message : `${tGlobal('payment_error_unexpected')}${codeSuffix}`;
}

/**
 * The other half of that policy: Stripe.js reports a failure it catches in the browser
 * — an IntegrationError, a bad parameter, an element that was never mounted — by
 * THROWING instead of resolving with `{ error }`. Stripe's API is never called, so
 * nothing lands in the Stripe log either, and the callers only show a generic toast.
 * DAB-957's hard fail was this shape, and until this was reported it reached no one
 * (DAB-971). Always ours to diagnose, so always reported.
 */
function reportThrownStripeError(thrown: unknown, context: string): string {
  if (thrown && typeof thrown === 'object') handledStripeErrors.add(thrown);
  const name = thrown instanceof Error ? thrown.name : typeof thrown;
  const message = thrown instanceof Error ? thrown.message : String(thrown);
  logError(new Error(`Stripe ${context} threw: ${name}`), { message, thrown });
  // Keep the reason beside the generic line, as for a returned error's code, so a
  // support screenshot still says what happened (DAB-957).
  const generic = tGlobal('payment_error_unexpected');
  return message ? `${generic} (${message})` : generic;
}

/**
 * Every Stripe error the two helpers above have already dealt with — reported to
 * Sentry, or deliberately not because it is the customer's to fix. The store still
 * rethrows them for control flow, so a caller that logs whatever it catches would
 * report the same failure a second time under a different title.
 */
const handledStripeErrors = new WeakSet<object>();

/** True when the payment store already reported (or chose not to report) this error. */
export function isHandledStripeError(err: unknown): boolean {
  return !!err && typeof err === 'object' && handledStripeErrors.has(err);
}

interface PaymentState {
  stripe: Stripe | null;
  elements: StripeElements | null;
  paymentElement: StripePaymentElement | null;
  clientSecret: string;
  customerSessionClientSecret: string;
  /**
   * True from the moment Stripe accepts the SetupIntent in `clientSecret`. A confirmed
   * SetupIntent is spent — confirming it again is a guaranteed
   * `setup_intent_unexpected_state` — so anything that fails after this point has to
   * rebuild the form on a fresh one before a retry can work (DAB-1407). Set here, not by
   * the caller, because the follow-up request in confirmSetup can fail after Stripe has
   * already confirmed.
   */
  setupIntentConsumed: boolean;
  saving: boolean;
  error: string | null;
}
export const usePaymentStore = defineStore('payment', {
  state: (): PaymentState => ({
    stripe: null,
    elements: null,
    paymentElement: null,
    clientSecret: '',
    customerSessionClientSecret: '',
    setupIntentConsumed: false,
    saving: false,
    error: null,
  }),

  actions: {
    async initializeStripe(publishableKey: string) {
      const stripe = await loadStripe(publishableKey);
      if (!stripe) {
        throw new Error('Failed to initialize Stripe');
      }
      this.stripe = stripe;
      return stripe;
    },

    async createSetupIntent() {
      try {
        const userStore = useUserStore();
        const response: any = await rest
          .post('/billing/payment-methods/setup')
          .header('x-uid', userStore.user?.uid || '')
          .credentials('include')
          .send();
        this.clientSecret = response.clientSecret;
        this.customerSessionClientSecret = response.customerSessionClientSecret ?? '';
        // `setupIntentConsumed` describes the intent in `clientSecret`, so it has to be
        // cleared wherever that changes — not only in teardownPaymentForm. Otherwise the
        // flag is correct only because callers happen to tear down before rebuilding.
        this.setupIntentConsumed = false;
        return this.clientSecret;
      } catch (error) {
        // Now that the Alert body actually renders (DAB-957) this reaches the
        // customer, so it has to be a localized string, not a developer note.
        // `init()` in use/stripeCheckout already logErrors the throw.
        this.error = tGlobal('billing_error_generic');
        throw error;
      }
    },

    async confirmSetup(
      returnUrl: string,
      elements?: StripeElements,
      // The card widget no longer collects an address (fields.billingDetails.address
      // = 'never'), so it must be supplied here for the payment method.
      billingDetails?: BillingDetailsInput,
    ) {
      // Express checkout confirms against its own elements group (see stripeCheckout).
      const els = elements ?? this.elements;
      if (!this.stripe || !els) {
        throw new Error('Stripe not initialized');
      }

      this.saving = true;
      try {
        const params = {
          elements: els,
          clientSecret: this.clientSecret,
          confirmParams: {
            return_url: returnUrl,
            ...(billingDetails
              ? { payment_method_data: { billing_details: toConfirmBillingDetails(billingDetails) } }
              : {}),
          },
          redirect: 'if_required' as const,
        };
        let error: StripeError | undefined;
        // try/catch rather than .catch(): Stripe.js may throw synchronously, before it
        // hands back a promise, and that must be reported the same as a rejection.
        try {
          ({ error } = await this.stripe.confirmSetup(params));
        } catch (thrown) {
          this.error = reportThrownStripeError(thrown, 'confirmSetup');
          throw thrown;
        }
        // Stripe has confirmed (or is confirming) the intent: it is spent from here on,
        // whatever happens to the default-method request below.
        if (!error) this.setupIntentConsumed = true;

        if (error) {
          this.error = describeAndReportStripeError(error, 'confirmSetup');
          throw error;
        }
        const userStore = useUserStore();

        // Set as default payment method
        await rest
          .post('/billing/payment-methods/last/default')
          .header('x-uid', userStore.user?.uid || '')
          .credentials('include')
          .send();

        return true;
      } catch (error) {
        throw error;
      } finally {
        this.saving = false;
      }
    },

    /**
     * Finish a first subscription payment Stripe could not take off-session. The
     * server (POST /billing/checkout/activate) hands back the invoice PaymentIntent's
     * client secret when the card needs the customer present — 3DS under SCA, or the
     * one-time authentication that registers an RBI e-mandate on an India-issued card.
     * Stripe.js opens the bank's challenge and resolves once it's done (DAB-1403).
     */
    async handleNextAction(clientSecret: string) {
      if (!this.stripe) {
        throw new Error('Stripe not initialized');
      }
      this.saving = true;
      try {
        let error: StripeError | undefined;
        // try/catch, not .catch(), so a synchronous Stripe.js throw is caught too.
        try {
          ({ error } = await this.stripe.handleNextAction({ clientSecret }));
        } catch (thrown) {
          this.error = reportThrownStripeError(thrown, 'handleNextAction');
          throw thrown;
        }
        if (error) {
          // An abandoned or failed challenge is a card_error written for the customer;
          // anything else is ours to diagnose. Same policy as confirmSetup.
          this.error = describeAndReportStripeError(error, 'handleNextAction');
          throw error;
        }
        return true;
      } finally {
        this.saving = false;
      }
    },

    setElements(elements: StripeElements) {
      this.elements = elements;
    },

    setPaymentElement(element: StripePaymentElement) {
      this.paymentElement = element;
    },

    /** Destroy mounted Elements UI and clear secrets (e.g. modal closed or component unmounted). */
    teardownPaymentForm() {
      if (this.paymentElement) {
        try {
          this.paymentElement.destroy();
        } catch {
          /* ignore */
        }
      }
      this.paymentElement = null;
      this.elements = null;
      this.clientSecret = '';
      this.customerSessionClientSecret = '';
      this.setupIntentConsumed = false;
    },

    clearError() {
      this.error = null;
    },
  },
});
